import { networkLogger } from '../logging';
import { env } from '../../config/env';
import { ApiResponse, API_STATUS_CODES } from '../../types/api';

export interface FetchOptions extends RequestInit {
  baseUrl?: string;
  endpoint: string;
  params?: Record<string, string | number | boolean | string[] | undefined>;
  responseType?: 'json' | 'text' | 'blob';
  validateApiResponse?: boolean;
}

export interface FetchError extends Error {
  status?: number;
  statusText?: string;
  url?: string;
  responseBody?: string;
  apiStatusCode?: string;
  apiMessage?: string;
  originalError?: Error | unknown;
  isApiError?: boolean;
}

/**
 * Formats query parameters for URL
 */
export function formatQueryParams(params?: Record<string, string | number | boolean | string[] | undefined>): string {
  if (!params) return '';
  
  const stringParams = Object.fromEntries(
    Object.entries(params)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => [
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      ])
  );
  
  return new URLSearchParams(stringParams).toString();
}

/**
 * Gets a user-friendly error message based on HTTP status code
 */
export function getErrorMessageForStatus(status: number): string {
  switch (status) {
    case 404:
      return 'errors.notFound';
    case 403:
      return 'errors.forbidden';
    case 401:
      return 'errors.unauthorized';
    case 400:
      return 'errors.badRequest';
    default:
      return status >= 500 ? 'errors.serverError' : 'errors.somethingWentWrong';
  }
}

/**
 * Enhanced fetch wrapper with consistent error handling and logging
 */
export async function fetchWithErrorHandling<T>(options: FetchOptions): Promise<T> {
  const {
    baseUrl = env.apiBaseUrl,
    endpoint,
    params,
    method = 'GET',
    headers = {},
    body,
    responseType = 'json',
    validateApiResponse = true,
    ...restOptions
  } = options;

  // Build URL with query parameters
  const queryString = params ? formatQueryParams(params) : '';
  const url = queryString
    ? `${baseUrl}${endpoint}?${queryString}`
    : `${baseUrl}${endpoint}`;

  // Set default headers
  const defaultHeaders: HeadersInit = {
    'Accept': 'application/json',
    ...(method !== 'GET' && method !== 'HEAD' && { 'Content-Type': 'application/json' }),
    ...headers
  };

  try {
    // Make the request
    const response = await fetch(url, {
      method,
      headers: defaultHeaders,
      body: body instanceof FormData ? body : (body ? JSON.stringify(body) : undefined),
      ...restOptions
    });

    // Handle HTTP errors
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No response body');
      const errorKey = getErrorMessageForStatus(response.status);
      
      // Create enhanced error object
      const error = new Error(errorKey) as FetchError;
      error.status = response.status;
      error.statusText = response.statusText;
      error.url = url;
      error.responseBody = errorText;
      error.name = 'FetchError';
      error.isApiError = true; // Mark as API error
      
      // Log detailed error information
      networkLogger.error(
        `API Request Failed: ${errorKey}`,
        error,
        {
          url,
          endpoint,
          params,
          method,
          status: response.status,
          statusText: response.statusText,
          responseBody: errorText,
          ...(body && { requestBody: typeof body === 'string' ? body.substring(0, 500) : JSON.stringify(body).substring(0, 500) })
        }
      );
      
      // Also log to console in development
      if (import.meta.env.DEV) {
        console.error('API Request Failed:', {
          url,
          status: response.status,
          statusText: response.statusText,
          errorKey,
          responseBody: errorText,
          ...(body && { requestBody: body })
        });
      }
      
      throw error;
    }

    // Parse response based on responseType
    let data: any;
    if (responseType === 'json') {
      data = await response.json();
    } else if (responseType === 'text') {
      data = await response.text();
    } else if (responseType === 'blob') {
      data = await response.blob();
    }
    
    // Validate API response if needed
    if (validateApiResponse && responseType === 'json') {
      const apiResponse = data as ApiResponse<T>;
      
      if (apiResponse.statusCode !== API_STATUS_CODES.SUCCESS) {
        // Create enhanced error object for API-level errors
        const error = new Error(apiResponse.message || 'API request failed') as FetchError;
        error.apiStatusCode = apiResponse.statusCode;
        error.apiMessage = apiResponse.message;
        error.url = url;
        error.name = 'ApiError';
        error.isApiError = true; // Mark as API error
        
        // Log API-level error
        networkLogger.error(
          'API Response Error',
          error,
          {
            url,
            endpoint,
            params,
            method,
            apiStatusCode: apiResponse.statusCode,
            apiMessage: apiResponse.message
          }
        );
        
        // Also log to console in development
        if (import.meta.env.DEV) {
          console.error('API Response Error:', {
            url,
            apiStatusCode: apiResponse.statusCode,
            message: apiResponse.message,
            data: apiResponse.data
          });
        }
        
        throw error;
      }
      
      return apiResponse.data as T;
    }
    
    return data;
  } catch (error) {
    // Handle errors that weren't already handled above
    if (!(error as Error).name || !['FetchError', 'ApiError'].includes((error as Error).name)) {
      const wrappedError = new Error(
        error instanceof Error ? error.message : String(error)
      ) as FetchError;
      wrappedError.name = 'NetworkError';
      wrappedError.originalError = error;
      wrappedError.url = url;
      wrappedError.isApiError = true; // Mark as API error
      
      networkLogger.error(
        'API Request Exception',
        wrappedError,
        {
          url,
          endpoint,
          params,
          method,
          errorType: error instanceof Error ? error.name : typeof error,
          originalError: error
        }
      );
      
      // Also log to console in development
      if (import.meta.env.DEV) {
        console.error('API Request Exception:', {
          url,
          error,
          endpoint,
          params,
          method
        });
      }
      
      throw wrappedError;
    }
    
    throw error;
  }
}

/**
 * Shorthand for GET requests
 */
export async function get<T>(endpoint: string, params?: Record<string, string | number | boolean | string[] | undefined>, options?: Partial<FetchOptions>): Promise<T> {
  return fetchWithErrorHandling<T>({
    endpoint,
    params,
    method: 'GET',
    ...options
  });
}

/**
 * Shorthand for POST requests
 */
export async function post<T>(endpoint: string, data?: any, options?: Partial<FetchOptions>): Promise<T> {
  return fetchWithErrorHandling<T>({
    endpoint,
    method: 'POST',
    body: data,
    ...options
  });
}

/**
 * Shorthand for PUT requests
 */
export async function put<T>(endpoint: string, data?: any, options?: Partial<FetchOptions>): Promise<T> {
  return fetchWithErrorHandling<T>({
    endpoint,
    method: 'PUT',
    body: data,
    ...options
  });
}

/**
 * Shorthand for DELETE requests
 */
export async function del<T>(endpoint: string, options?: Partial<FetchOptions>): Promise<T> {
  return fetchWithErrorHandling<T>({
    endpoint,
    method: 'DELETE',
    ...options
  });
}

/**
 * Shorthand for PATCH requests
 */
export async function patch<T>(endpoint: string, data?: any, options?: Partial<FetchOptions>): Promise<T> {
  return fetchWithErrorHandling<T>({
    endpoint,
    method: 'PATCH',
    body: data,
    ...options
  });
}